/**
 * The ONE boolean-literal policy for filter SQL, shared by both emitters that
 * must agree byte for byte: the renderer's `filterWhereBuilder.ts` (Visual Query
 * Editor chips, Data Profile, JSON scans) and the Table Explorer generators'
 * escapers in `electron/table-explorer/queryBuilder/` (grid SELECT and the
 * pagination COUNT). A JS boolean reaches them from a boolean-typed column's
 * switch, or from a filter persisted with one.
 *
 * - PostgreSQL — `TRUE` / `FALSE`. `boolean = 1` is an error (no `boolean =
 *   integer` operator).
 * - MySQL — `TRUE` / `FALSE`: aliases of `1` / `0`, so they compare correctly
 *   against `BOOL` / `TINYINT(1)`, `BIT(1)` and integer flags alike; the same
 *   spelling the MySQL script provider and data-compare emit.
 * - Oracle — `1` / `0`, on every version. Oracle 21c and older have no SQL
 *   `TRUE` / `FALSE` (`ORA-00904: "TRUE": invalid identifier`) and model
 *   flags as `NUMBER(1)`; 23ai added the SQL `BOOLEAN` type, which accepts
 *   `1` / `0` through implicit conversion (`b = 1`, `b <> 0`, `b IN (1)` —
 *   measured on 23.26). So `1` / `0` is right for a `NUMBER(1)` flag and a
 *   native `BOOLEAN` on every server, where `TRUE` / `FALSE` would break on
 *   pre-23 servers and gains nothing on 23+.
 * - SQL Server — `1` / `0`: `bit`; T-SQL has no `TRUE` / `FALSE` literals.
 * - SQLite — `1` / `0`: booleans are integers (the `TRUE` / `FALSE` keywords
 *   only exist from 3.23).
 *
 * Kusto builds KQL, not SQL, in `kustoFilterMapping.ts` with its own `true` /
 * `false`, and Azure Storage builds OData filters in its own Table host; they
 * (and the test-only `mock` engine) keep the numeric default here.
 */

import type { DatabaseEngineId } from '../engines/engineTypes';

export function sqlBooleanLiteral(value: boolean, engine: DatabaseEngineId): string {
  switch (engine) {
    case 'pgsql':
    case 'mysql':
      return value ? 'TRUE' : 'FALSE';
    case 'oracle':
    case 'mssql':
    case 'sqlite':
    case 'kusto':
    case 'azurestorage':
    case 'mock':
      return value ? '1' : '0';
  }
}
